import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import { useEventGenreColors, findGenreColor } from '../../../hooks/useGenreColors';
import { artistsData } from '../../../mocks/artists';
import type { PrefectureEvent } from '../../../mocks/events';
import EventCalendarView from './EventCalendarView';

// ─── 型定義 ───────────────────────────────────────────────────────────────────

interface EventAttribute {
  id: string;
  name: string;
  color: string;
}

interface EnrichedEvent extends PrefectureEvent {
  attributeName?: string;
  mainGenreName?: string;
  mainPartName?: string;
  displayColor?: string;
}

// ─── 定数 ─────────────────────────────────────────────────────────────────────

const DEFAULT_COLOR = '#00d9ff';

const categoryIconMap: Record<string, string> = {
  'ALL': 'ri-calendar-event-line',
  'LIVE': 'ri-music-2-line',
  'DJ EVENT': 'ri-disc-line',
  'DJ': 'ri-disc-line',
  'WORKSHOP': 'ri-tools-line',
  'MARKET': 'ri-store-2-line',
  'EXHIBITION': 'ri-gallery-line',
  'ライブ': 'ri-music-2-line',
  'ワークショップ': 'ri-tools-line',
  'フェス': 'ri-flag-line',
  'トークイベント': 'ri-chat-3-line',
  '展示': 'ri-gallery-line',
};

// ─── ユーティリティ ────────────────────────────────────────────────────────────

function formatDate(dateStr: string): { month: string; day: string; weekday: string } {
  const date = new Date(dateStr);
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const weekdays = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
  return {
    month: months[date.getMonth()],
    day: String(date.getDate()),
    weekday: weekdays[date.getDay()],
  };
}

function calcCountdown(dateStr: string): { days: number; status: 'future' | 'today' | 'past' } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDate = new Date(dateStr);
  const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  const diffMs = eventDay.getTime() - today.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return { days: 0, status: 'today' };
  if (days > 0) return { days, status: 'future' };
  return { days: Math.abs(days), status: 'past' };
}

function getArtistDisplayNames(event: PrefectureEvent): string[] {
  const names: string[] = [];
  for (const id of event.artistIds) {
    const found = artistsData.find((a) => a.id === id);
    if (found) names.push(found.name);
  }
  for (const name of event.artistNames) {
    names.push(name);
  }
  return names;
}

// ─── 期間フィルター定義 ────────────────────────────────────────────────────────

type PeriodFilter = 'all' | 'today' | 'this_week' | 'this_month' | 'next_month';

const periodFilters: Array<{ id: PeriodFilter; label: string; icon: string }> = [
  { id: 'all',        label: 'すべて',   icon: 'ri-infinity-line' },
  { id: 'today',      label: '本日開催', icon: 'ri-live-line' },
  { id: 'this_week',  label: '今週開催', icon: 'ri-calendar-check-line' },
  { id: 'this_month', label: '今月開催', icon: 'ri-calendar-2-line' },
  { id: 'next_month', label: '来月開催', icon: 'ri-calendar-line' },
];

function isInPeriod(dateStr: string, period: PeriodFilter): boolean {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDate = new Date(dateStr);
  const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());

  if (period === 'all') return true;

  if (period === 'today') {
    return eventDay.getTime() === today.getTime();
  }

  if (period === 'this_week') {
    const dayOfWeek = today.getDay(); // 0=Sun
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - dayOfWeek);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return eventDay >= weekStart && eventDay <= weekEnd;
  }

  if (period === 'this_month') {
    return (
      eventDay.getFullYear() === today.getFullYear() &&
      eventDay.getMonth() === today.getMonth()
    );
  }

  if (period === 'next_month') {
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return (
      eventDay.getFullYear() === nextMonth.getFullYear() &&
      eventDay.getMonth() === nextMonth.getMonth()
    );
  }

  return true;
}

// ─── 並び替え定義 ──────────────────────────────────────────────────────────────

type SortOrder = 'date_asc' | 'date_desc' | 'popular' | 'newest' | 'price_asc' | 'price_free_first';

const sortOptions: Array<{ id: SortOrder; label: string; icon: string }> = [
  { id: 'date_asc',        label: '開催日が近い順',   icon: 'ri-calendar-check-line' },
  { id: 'date_desc',       label: '開催日が遠い順',   icon: 'ri-calendar-line' },
  { id: 'newest',          label: '新着順',           icon: 'ri-time-line' },
  { id: 'popular',         label: '人気順',           icon: 'ri-fire-line' },
  { id: 'price_free_first',label: '無料優先',         icon: 'ri-gift-line' },
  { id: 'price_asc',       label: '価格が安い順',     icon: 'ri-price-tag-3-line' },
];

function parsePriceValue(price: string): number {
  if (!price) return Infinity;
  const lower = price.toLowerCase();
  if (lower.includes('無料') || lower.includes('free') || lower === '0') return 0;
  const match = price.replace(/,/g, '').match(/\d+/);
  return match ? parseInt(match[0], 10) : Infinity;
}

// ─── EventCard ────────────────────────────────────────────────────────────────

function EventCard({ event, genreColorMap }: { event: EnrichedEvent; genreColorMap: Record<string, string> }) {
  const navigate = useNavigate();
  const { month, day, weekday } = formatDate(event.date);
  const countdown = calcCountdown(event.date);
  const iconKey = event.attributeName || event.category;
  const icon = categoryIconMap[iconKey] || 'ri-calendar-line';
  const genreColor = event.displayColor || findGenreColor(event.category, genreColorMap, event.color);

  const countdownBadge = (() => {
    if (countdown.status === 'today') {
      return { label: '本日開催！', icon: 'ri-live-line', color: '#00ff41', pulse: true };
    }
    if (countdown.status === 'future') {
      if (countdown.days <= 3) return { label: `あと ${countdown.days} 日`, icon: 'ri-alarm-line', color: '#ff4444', pulse: true };
      if (countdown.days <= 7) return { label: `あと ${countdown.days} 日`, icon: 'ri-time-line', color: '#ff9500', pulse: false };
      if (countdown.days <= 30) return { label: `あと ${countdown.days} 日`, icon: 'ri-calendar-event-line', color: genreColor, pulse: false };
    }
    return null;
  })();

  return (
    <div
      className="w-full rounded-golden-lg border overflow-hidden transition-all duration-300 cursor-pointer group hover:-translate-y-1"
      style={{ borderColor: `${genreColor}20`, background: '#0d0d0d' }}
      onClick={() => navigate(`/event/${event.id}`)}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${genreColor}20`;
        (e.currentTarget as HTMLElement).style.borderColor = `${genreColor}50`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLElement).style.borderColor = `${genreColor}20`;
      }}
    >
      {/* Thumbnail */}
      <div className="relative w-full h-[160px] overflow-hidden">
        <img
          src={event.thumbnail}
          alt={event.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-black/30 to-transparent" />

        {/* Badges */}
        <div className="absolute top-golden-sm left-golden-sm flex items-center gap-golden-xs">
          {event.isFeatured && (
            <div className="px-golden-sm py-golden-xxs rounded-golden-full bg-green-400/20 border border-green-400/40 backdrop-blur-sm">
              <span className="text-green-400 text-[8px] font-bold tracking-wider">FEATURED</span>
            </div>
          )}
          {event.created_by && (
            <div className="px-golden-sm py-golden-xxs rounded-golden-full backdrop-blur-sm flex items-center gap-golden-xxs"
              style={{ background: 'rgba(0,217,255,0.15)', border: '1px solid rgba(0,217,255,0.4)' }}>
              <span className="w-1 h-1 rounded-full bg-[#00d9ff]" style={{ boxShadow: '0 0 4px #00d9ff' }} />
              <span className="text-[#00d9ff] text-[8px] font-bold tracking-wider">NODE EVENT</span>
            </div>
          )}
        </div>

        {/* Category badge */}
        <div className="absolute top-golden-sm right-golden-sm">
          <span
            className="px-golden-sm py-golden-xxs rounded-golden-full text-[10px] font-bold tracking-wider backdrop-blur-sm"
            style={{ background: `${genreColor}30`, color: genreColor, border: `1px solid ${genreColor}40` }}
          >
            <i className={`${icon} mr-1`} />
            {event.attributeName || event.category}
          </span>
        </div>

        {/* Date + countdown */}
        <div className="absolute bottom-golden-sm left-golden-sm flex items-center gap-golden-sm">
          <div
            className="w-12 h-14 rounded-golden-base flex flex-col items-center justify-center backdrop-blur-md"
            style={{ background: `${genreColor}15`, border: `1px solid ${genreColor}30` }}
          >
            <span className="text-[9px] tracking-wider" style={{ color: genreColor }}>{month}</span>
            <span className="text-white text-lg font-bold leading-none">{day}</span>
            <span className="text-gray-400 text-[8px] tracking-wider">{weekday}</span>
          </div>
          {countdownBadge && (
            <div
              className={`inline-flex items-center gap-golden-xxs px-golden-sm py-golden-xs rounded-golden-full text-[9px] font-bold tracking-wider backdrop-blur-md ${countdownBadge.pulse ? 'animate-pulse' : ''}`}
              style={{
                background: `${countdownBadge.color}18`,
                color: countdownBadge.color,
                border: `1px solid ${countdownBadge.color}60`,
                boxShadow: `0 0 10px ${countdownBadge.color}25`,
              }}
            >
              <i className={`${countdownBadge.icon} text-[9px]`} />
              {countdownBadge.label}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-golden-md">
        <h4 className="text-white text-sm font-bold tracking-wide mb-golden-xs truncate">{event.title}</h4>
        <p className="text-gray-500 text-xs leading-relaxed mb-golden-sm line-clamp-2 min-h-[2.5rem]">{event.description}</p>

        {getArtistDisplayNames(event).length > 0 && (
          <div className="flex flex-wrap gap-golden-xs mb-golden-sm">
            {getArtistDisplayNames(event).map((name) => (
              <span key={name} className="px-golden-sm py-golden-xxs rounded-golden-full text-[10px] tracking-wider bg-white/5 text-gray-400 border border-white/10">
                {name}
              </span>
            ))}
          </div>
        )}

        <div className="space-y-golden-xs pt-golden-sm border-t border-white/5">
          <div className="flex items-center gap-golden-xs">
            <i className="ri-map-pin-line text-gray-600 text-xs w-3 h-3 flex items-center justify-center" />
            <span className="text-gray-500 text-[11px] truncate">{event.venue}</span>
          </div>
          <div className="flex items-center gap-golden-xs">
            <i className="ri-time-line text-gray-600 text-xs w-3 h-3 flex items-center justify-center" />
            <span className="text-gray-500 text-[11px]">{event.time}</span>
          </div>
          <div className="flex items-center gap-golden-xs mt-golden-xs">
            <i className="ri-ticket-line text-xs w-3 h-3 flex items-center justify-center" style={{ color: genreColor }} />
            <span className="text-xs font-bold tracking-wider" style={{ color: genreColor }}>{event.price}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EventsTab (メイン) ───────────────────────────────────────────────────────

export default function EventsTab() {
  const [events, setEvents] = useState<EnrichedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [activePeriod, setActivePeriod] = useState<PeriodFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('date_asc');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [attributes, setAttributes] = useState<EventAttribute[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const genreColorMap = useEventGenreColors();

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Supabaseから未来のイベントを取得
  useEffect(() => {
    const fetchFutureEvents = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];

        const { data, error } = await supabase
          .from('events')
          .select('*')
          .gte('date', today)
          .order('date', { ascending: true });

        if (error) throw error;

        const rawEvents: PrefectureEvent[] = (data || []).map((item: any) => ({
          id: item.id,
          title: item.title || '',
          date: item.date || '',
          time: item.time || '',
          venue: item.venue || '',
          address: item.address || '',
          prefecture: item.prefecture || '',
          description: item.description || '',
          price: item.price || '',
          category: item.category || 'LIVE',
          thumbnail: item.thumbnail || item.image_url || '',
          gallery: item.gallery || [],
          artistIds: item.artist_ids || [],
          artistNames: item.artist_names || [],
          isFeatured: item.is_featured || false,
          color: item.color || DEFAULT_COLOR,
          attributeId: item.attribute_id || '',
          mainGenreId: item.main_genre_id || '',
          mainPartId: item.main_part_id || '',
          is_paid_event: item.is_paid_event || false,
          ticketPrice: item.ticket_price ?? null,
          maxCapacity: item.max_capacity ?? null,
          currentReservations: item.current_reservations || 0,
          soldOut: item.sold_out || false,
          createdBy: item.created_by || null,
        }));

        // 属性・ジャンル・パートを一括取得してエンリッチ
        const attributeIds = [...new Set(rawEvents.map(e => e.attributeId).filter(Boolean))];
        const genreIds = [...new Set(rawEvents.map(e => e.mainGenreId).filter(Boolean))];
        const partIds = [...new Set(rawEvents.map(e => e.mainPartId).filter(Boolean))];

        const [attrRes, genreRes, partRes] = await Promise.all([
          attributeIds.length > 0 ? supabase.from('event_attributes').select('id, name, color').in('id', attributeIds) : { data: [] },
          genreIds.length > 0 ? supabase.from('event_main_genres').select('id, name').in('id', genreIds) : { data: [] },
          partIds.length > 0 ? supabase.from('event_main_parts').select('id, name').in('id', partIds) : { data: [] },
        ]);

        const attrMap = new Map((attrRes.data || []).map((a: any) => [a.id, a]));
        const genreMap = new Map((genreRes.data || []).map((g: any) => [g.id, g]));
        const partMap = new Map((partRes.data || []).map((p: any) => [p.id, p]));

        setAttributes(Array.from(attrMap.values()) as EventAttribute[]);

        const enriched: EnrichedEvent[] = rawEvents.map(event => {
          const attr = event.attributeId ? attrMap.get(event.attributeId) as any : null;
          const genre = event.mainGenreId ? genreMap.get(event.mainGenreId) as any : null;
          const part = event.mainPartId ? partMap.get(event.mainPartId) as any : null;
          return {
            ...event,
            attributeName: attr?.name,
            mainGenreName: genre?.name,
            mainPartName: part?.name,
            displayColor: attr?.color || event.color,
          };
        });

        setEvents(enriched);
      } catch (err) {
        console.error('Failed to fetch future events:', err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFutureEvents();
  }, []);

  const categories = useMemo(() => {
    const cats: Array<{ id: string; name: string; color: string }> = [
      { id: 'ALL', name: 'すべて', color: DEFAULT_COLOR },
    ];
    attributes.forEach(attr => cats.push({ id: attr.id, name: attr.name, color: attr.color }));
    return cats;
  }, [attributes]);

  const filteredEvents = useMemo(() => {
    let result = activeCategory === 'ALL'
      ? events
      : events.filter(e => e.attributeId === activeCategory);

    // 期間フィルター
    if (activePeriod !== 'all') {
      result = result.filter(e => isInPeriod(e.date, activePeriod));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e =>
        e.title.toLowerCase().includes(q) ||
        e.venue.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        getArtistDisplayNames(e).some(n => n.toLowerCase().includes(q))
      );
    }

    // 並び替え
    const sorted = [...result];
    switch (sortOrder) {
      case 'date_asc':
        sorted.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 'date_desc':
        sorted.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 'newest':
        sorted.sort((a, b) => b.id.localeCompare(a.id));
        break;
      case 'popular':
        sorted.sort((a, b) => {
          const aScore = (a.isFeatured ? 100 : 0) + (a.current_reservations || 0);
          const bScore = (b.isFeatured ? 100 : 0) + (b.current_reservations || 0);
          return bScore - aScore;
        });
        break;
      case 'price_free_first':
        sorted.sort((a, b) => {
          const aVal = parsePriceValue(a.price);
          const bVal = parsePriceValue(b.price);
          if (aVal === 0 && bVal !== 0) return -1;
          if (bVal === 0 && aVal !== 0) return 1;
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });
        break;
      case 'price_asc':
        sorted.sort((a, b) => parsePriceValue(a.price) - parsePriceValue(b.price));
        break;
      default:
        break;
    }
    return sorted;
  }, [events, activeCategory, activePeriod, searchQuery, sortOrder]);

  const currentSort = sortOptions.find(s => s.id === sortOrder) || sortOptions[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-7xl mx-auto px-golden-md py-golden-xl"
    >
      {/* Header */}
      <div className="mb-golden-lg">
        <div className="flex items-center gap-golden-sm mb-golden-xs">
          <div className="w-1.5 h-1.5 rounded-full bg-teal-400" style={{ boxShadow: '0 0 8px #2dd4bf' }} />
          <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Upcoming Events</span>
        </div>
        <div className="flex items-center justify-between">
          <h2 className="text-white font-bold text-xl tracking-wider">EVENTS — 開催予定</h2>
          <div className="flex items-center gap-golden-sm">
            {!loading && (
              <span className="text-teal-400 text-sm font-bold">{filteredEvents.length} 件</span>
            )}
            {/* カレンダーボタン */}
            <button
              onClick={() => setCalendarOpen(true)}
              className="flex items-center gap-golden-xs px-golden-sm py-golden-xs rounded-golden-base border border-teal-500/30 bg-teal-500/10 hover:bg-teal-500/20 hover:border-teal-500/50 transition-all cursor-pointer whitespace-nowrap"
              style={{ boxShadow: '0 0 12px rgba(45,212,191,0.1)' }}
            >
              <i className="ri-calendar-2-line text-teal-400 text-sm w-4 h-4 flex items-center justify-center" />
              <span className="text-teal-400 text-xs font-bold tracking-wider">カレンダーから探す</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search + Sort Row */}
      <div className="flex items-center gap-golden-sm mb-golden-md">
        {/* Search */}
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-golden-sm top-1/2 -translate-y-1/2 text-gray-500 text-sm w-4 h-4 flex items-center justify-center" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="イベント名・会場・アーティストで検索..."
            className="w-full bg-white/5 border border-white/10 rounded-golden-base pl-9 pr-golden-md py-golden-xs text-sm text-white placeholder-gray-600 focus:outline-none focus:border-teal-500/50 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-golden-sm top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 cursor-pointer w-4 h-4 flex items-center justify-center"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        {/* Sort Dropdown */}
        <div className="relative flex-shrink-0" ref={sortDropdownRef}>
          <button
            onClick={() => setSortDropdownOpen(prev => !prev)}
            className="flex items-center gap-golden-xs px-golden-sm py-golden-xs rounded-golden-base border border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className={`${currentSort.icon} text-teal-400 text-sm w-4 h-4 flex items-center justify-center`} />
            <span className="text-gray-300 text-xs font-bold tracking-wider hidden sm:inline">{currentSort.label}</span>
            <i className={`ri-arrow-down-s-line text-gray-500 text-sm w-4 h-4 flex items-center justify-center transition-transform duration-200 ${sortDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {sortDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-golden-xs w-52 rounded-golden-lg border border-white/10 overflow-hidden z-30"
              style={{ background: '#111111', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}
            >
              <div className="px-golden-sm py-golden-xs border-b border-white/5">
                <span className="text-gray-600 text-[10px] tracking-[0.2em] uppercase font-bold">並び替え</span>
              </div>
              {sortOptions.map(opt => {
                const isSelected = sortOrder === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => { setSortOrder(opt.id); setSortDropdownOpen(false); }}
                    className="w-full flex items-center gap-golden-sm px-golden-md py-golden-xs text-left transition-all cursor-pointer hover:bg-white/5"
                    style={isSelected ? { background: 'rgba(45,212,191,0.08)' } : undefined}
                  >
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`${opt.icon} text-sm`}
                        style={{ color: isSelected ? '#2dd4bf' : '#6b7280' }}
                      />
                    </div>
                    <span
                      className="text-xs font-medium tracking-wide"
                      style={{ color: isSelected ? '#2dd4bf' : '#9ca3af' }}
                    >
                      {opt.label}
                    </span>
                    {isSelected && (
                      <div className="ml-auto w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-teal-400 text-sm" />
                      </div>
                    )}
                  </button>
                );
              })}
            </motion.div>
          )}
        </div>
      </div>

      {/* ── 期間フィルター ── */}
      <div className="flex items-center gap-golden-xs mb-golden-sm flex-wrap">
        {periodFilters.map(pf => {
          const isActive = activePeriod === pf.id;
          const accentColor = '#2dd4bf';
          return (
            <button
              key={pf.id}
              onClick={() => setActivePeriod(pf.id)}
              className={`flex items-center gap-golden-xxs px-golden-sm py-golden-xs rounded-golden-full border text-xs font-bold tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'text-white'
                  : 'bg-transparent text-gray-500 border-white/10 hover:bg-white/5 hover:text-gray-300'
              }`}
              style={
                isActive
                  ? {
                      background: `${accentColor}18`,
                      borderColor: `${accentColor}60`,
                      color: accentColor,
                      boxShadow: `0 0 12px ${accentColor}20`,
                    }
                  : undefined
              }
            >
              <i
                className={`${pf.icon} text-xs w-3.5 h-3.5 flex items-center justify-center`}
                style={isActive ? { color: accentColor } : undefined}
              />
              {pf.label}
            </button>
          );
        })}
      </div>

      {/* Category filter */}
      <div className="relative mb-golden-lg">
        <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
        <div className="flex gap-golden-xs overflow-x-auto pb-2 px-1" style={{ scrollbarWidth: 'none' }}>
          {categories.map(cat => {
            const isActive = activeCategory === cat.id;
            const icon = categoryIconMap[cat.name] || categoryIconMap['ALL'];
            return (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); scrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' }); }}
                className={`flex items-center gap-golden-xxs px-golden-sm py-golden-xs rounded-golden-full border whitespace-nowrap text-xs font-bold tracking-wider transition-all duration-300 cursor-pointer flex-shrink-0 ${
                  isActive ? 'bg-white/10 text-white' : 'bg-transparent text-gray-500 border-white/10 hover:bg-white/5 hover:text-gray-300'
                }`}
                style={{
                  borderColor: isActive ? `${cat.color}60` : undefined,
                  boxShadow: isActive ? `0 0 16px ${cat.color}20` : undefined,
                }}
              >
                <i className={`${icon} text-sm w-4 h-4 flex items-center justify-center`} style={{ color: isActive ? cat.color : undefined }} />
                <span>{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="relative w-12 h-12 mb-golden-md">
            <div className="absolute inset-0 border-2 border-teal-400/20 rounded-full" />
            <div className="absolute inset-0 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-gray-600 text-xs tracking-wider">LOADING EVENTS...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 flex items-center justify-center mb-golden-md">
            <i className="ri-calendar-line text-5xl text-gray-700" />
          </div>
          <p className="text-gray-500 text-sm mb-golden-xxs">
            {searchQuery
              ? `"${searchQuery}" に一致するイベントはありません`
              : activePeriod !== 'all'
              ? `${periodFilters.find(p => p.id === activePeriod)?.label}のイベントはありません`
              : '開催予定のイベントはまだありません'}
          </p>
          <p className="text-gray-700 text-xs">近日公開予定のイベントをお楽しみに</p>
        </div>
      ) : (
        <>
          {/* Mobile: 縦並び */}
          <div className="sm:hidden flex flex-col gap-golden-md">
            {filteredEvents.map(event => (
              <EventCard key={event.id} event={event} genreColorMap={genreColorMap} />
            ))}
          </div>

          {/* PC: グリッド */}
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-golden-md">
            {filteredEvents.map(event => (
              <EventCard key={event.id} event={event} genreColorMap={genreColorMap} />
            ))}
          </div>
        </>
      )}

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* ── カレンダーモーダル ── */}
      <AnimatePresence>
        {calendarOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCalendarOpen(false)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div
              className="relative w-full max-w-2xl my-8 mx-golden-md rounded-golden-xl border border-teal-500/20 overflow-hidden"
              style={{ background: '#0a0a0a' }}
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              onClick={e => e.stopPropagation()}
            >
              {/* モーダルヘッダー */}
              <div className="flex items-center justify-between px-golden-lg py-golden-md border-b border-white/5">
                <div className="flex items-center gap-golden-sm">
                  <i className="ri-calendar-2-line text-teal-400 text-base w-5 h-5 flex items-center justify-center" />
                  <span className="text-white font-bold tracking-wider text-sm">カレンダーから探す</span>
                </div>
                <button
                  onClick={() => setCalendarOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-golden-full bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-gray-400" />
                </button>
              </div>

              {/* カレンダー本体 */}
              <EventCalendarView />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
